/**
 * A bundle's pieces turned into the records an import creates (decision 7, L10, L11, decision 29; part 24c log, tables P,
 * H and C, principles 3–5). Pure: the IDs, the time, the import's snapshot owner and SHA-256 are given. A
 * {@link BundleRestore} takes the pieces a batch at a time in table O′'s order (part 24b) and keeps only what later pieces
 * need (IDs met, per chapter and owner a count and a time, the last valid position, the recovered chapters' entries), never
 * the book (principle 5). Every record it makes is created (`add`, principle 2), never put.
 */
import { escapeLiteral, isPosition, spread } from '@cockatrice/core';
import { bookNoteNote, chapterNotesNote, clampTime, commentId, commentNote, exportPinUntil, isId, NOTE_KINDS } from '@cockatrice/storage';
import type { ChapterEntry, ImportOp, ImageRecord, NoteRecord, ShareRecord, Snapshot } from '@cockatrice/storage';
import type { JsonValue } from './json.ts';
import { shareLinkRecordOf, shareRecordOf } from './manifest.ts';
import type { ManifestTemplate } from './manifest.ts';
import type { BookNotePiece, BundlePiece, ChapterPiece, CommentPiece, CopyPiece, ImagePiece, NotePiece, RefusedPiece, ShareLinkPiece, SharePiece, SnapshotPiece } from './pieces.ts';

/** L10: the book restored with the bundle's IDs; L11: a copy with fresh ones (table C). */
export type RestoreMode = 'restore' | 'copy';

/** What a restore needs; everything with an effect is given. */
export interface RestoreOptions {
  /** The new book's ID: the bundle's (L10) or a fresh one (L11). */
  readonly bookId: string;
  readonly mode: RestoreMode;
  /** Table H: the import's own snapshot owner, a fresh ID (decision 29). */
  readonly owner: string;
  /** When the import runs, in milliseconds since the Unix epoch. */
  readonly now: number;
  /** A fresh ID (32 lowercase hex digits). */
  readonly newId: () => string;
  /** SHA-256: a copy's comment IDs (P10). */
  readonly digest: (bytes: Uint8Array) => Promise<Uint8Array>;
  /** P6: the most bytes the book's images may hold (`BOOK_IMAGE_CAP`). */
  readonly cap: number;
  /** Part 24d, H′2: the time zone offset at `now`, in minutes east of UTC. */
  readonly zone: number;
}

/** What a batch creates (P2–P22): its records, and the found pictures' bytes for the page's pipeline (P7, P21). */
export interface PlannedBatch {
  readonly ops: readonly ImportOp[];
  readonly found: readonly Uint8Array[];
}

/** What the import made, for the writer (the review's outcome). */
export interface RestoreCounts {
  /** Chapters in the book and in the trash, as the bundle has them (P2, P3). */
  readonly chapters: number;
  readonly trash: number;
  readonly copies: number;
  readonly snapshots: number;
  readonly notes: number;
  readonly comments: number;
  /** Images stored under their IDs (P6). */
  readonly pictures: number;
  /** Images with no bytes, or past the cap (P6, P8). */
  readonly missing: number;
  /** Found pictures handed to the pipeline (P7, P21). */
  readonly found: number;
  /** Refused records whose words came back as a chapter, a note, or a text of their own (P11–P19). */
  readonly recovered: number;
}

/** P23: what the page names the recovered chapters. */
export interface RecoveredLabels {
  /** A recovered chapter's title when its raw value gives none. */
  readonly text: string;
  /** The recovered pictures' chapter. */
  readonly pictures: string;
}

/** P1, P11: the book's metadata and theme texts the bundle offers, in the order to try them (part 05's readers decide). */
export interface BookTexts {
  readonly metadata: readonly string[];
  readonly theme: readonly string[];
}

/** Table H: one bundle owner's run in one chapter: its place among the chapter's owners, its count and last time. */
interface Run {
  readonly ordinal: number;
  count: number;
  last: number | undefined;
}

/** Table H: how far apart two owners' runs in a chapter lie in `seq`: more snapshots than any owner holds. */
export const OWNER_SPAN = 2 ** 32;

/**
 * Part 24d, H′2 (deep-fix r3): the `seq` of the import's hand-over snapshot of a chapter: past every run of the bundle's
 * owners (fewer than 2^20 in a chapter, the bundle's limits), so it is the newest of the import's owner there.
 */
export const HANDOVER_SEQ = 2 ** 20 * OWNER_SPAN;

/** A field of a refused record's raw value, when it is an object with that field of its own. */
function rawField(raw: JsonValue, key: string): unknown {
  return typeof raw === 'object' && raw !== null && Object.hasOwn(raw, key) ? Reflect.get(raw, key) : undefined;
}

/** A string field of a raw value, or `fallback`. */
function rawString(raw: JsonValue, key: string, fallback: string): string {
  const value = rawField(raw, key);
  return typeof value === 'string' ? value : fallback;
}

/** The value under `key` in `map`, taken out of it; null when there is none (or no key). */
function takeOut(map: Map<string, string>, key: string | null): string | null {
  if (key === null) return null;
  const value = map.get(key) ?? null;
  map.delete(key);
  return value;
}

/** A whole number from 0 at `key` of a raw value, or `fallback`. */
function rawCount(raw: JsonValue, key: string, fallback: number): number {
  const value = rawField(raw, key);
  return Number.isSafeInteger(value) && Number(value) >= 0 ? Number(value) + 0 : fallback;
}

/**
 * BU (L10, IN13), BV2: a share of a bundle as book `bookId` holds it on this device: as the bundle has it, its role keys
 * with their holders (the relay holds those wraps whichever device sent them), nothing pushed. Fix-next 84, BO5: made no
 * later than the import at `now`, so a bundle can't put its share ahead of every share the book makes after (the newest
 * open share is the one new notes and invites go to). Fix-next 85, BO7: null for a share never made at the relay (its
 * secret is whoever wrote the bundle's, and BO1 would let this device's session make it), and BO8: without the invites
 * never sent (their claim secrets and person keys are the bundle's writer's: this device sends only invites it made).
 */
export function restoredShare(share: SharePiece, bookId: string, now: number): ShareRecord | null {
  if (!share.made) return null;
  const record = shareRecordOf(share, bookId);
  return { ...record, createdAt: Math.min(record.createdAt, now), invites: sentInvites(record.invites), pushed: [] };
}

/** Fix-next 85, BO8: the invites of a bundle's share that were sent (the relay holds them); never one only the bundle's writer made. */
export function sentInvites<T extends { readonly sent: string | null }>(invites: readonly T[]): T[] {
  return invites.filter((invite) => invite.sent !== null);
}

/**
 * BV3 (L11's copy, part 38e): `note` as a copy of its book holds it: a copy isn't the shared book, so a note with a share
 * part becomes Only me, the part dropped. An invitee's note can't stand without one (NR8), and dropping it would lose its
 * words (tenet 3), so it becomes the writer's, its words led by its author's name and a colon when it has one (a
 * tombstone keeps none, NR6). A note with no share part is as it is.
 */
export function copiedNote(note: NoteRecord): NoteRecord {
  const { shared, ...rest } = note;
  if (shared === undefined) return note;
  if (note.origin !== 'invitee') return { ...rest, visibility: 'only-me' };
  return { ...rest, origin: 'writer', author: '', visibility: 'only-me', text: note.author === '' ? note.text : `${note.author}: ${note.text}` };
}

/** Tables P, H and C over one bundle's pieces. */
export class BundleRestore {
  protected readonly options: RestoreOptions;
  /** C1: each chapter ID the bundle names, to the book's (itself when restoring). */
  protected readonly chapters = new Map<string, string>();
  /** P14: chapters created with no text yet, by the bundle's ID, to the book's. */
  private readonly textless = new Map<string, string>();
  /** P17: snapshots created with no text yet, by the bundle's ID, to the book's. */
  private readonly textlessSnapshots = new Map<string, string>();
  /** Table H: each chapter's owners' runs, by the book's chapter ID, then the bundle's owner. */
  private readonly runs = new Map<string, Map<string, Run>>();
  /** P23: the greatest valid position of a chapter in the book. */
  protected last: string | null = null;
  /** Part 19e: each note ID the bundle names, to the book's (C2: itself when restoring), so a reply finds its thread. */
  protected readonly notes = new Map<string, string>();
  /** P12–P19, P23: the recovered chapters' entries (their texts are created as met), in the order found. */
  private readonly recovered: Omit<ChapterEntry, 'position'>[] = [];
  private readonly texts: { metadata: string[]; theme: string[] } = { metadata: [], theme: [] };
  /** TL17–TL20: the book piece's template link. */
  private link: ManifestTemplate | null = null;
  protected used = 0;
  protected readonly tally = { chapters: 0, trash: 0, copies: 0, snapshots: 0, notes: 0, comments: 0, pictures: 0, missing: 0, found: 0, recovered: 0 };

  constructor(options: RestoreOptions) {
    this.options = options;
  }

  /** What the import made so far. */
  counts(): RestoreCounts {
    return { ...this.tally };
  }

  /**
   * P6, P7: whether `size` more bytes of pictures fit the book under the cap, taken when they do; the kept images and the
   * found pictures share it, in the order met.
   */
  reserve(size: number): boolean {
    if (this.used + size > this.options.cap) return false;
    this.used += size;
    return true;
  }

  /** P1, P11: the metadata and theme texts met, the book's first, then a refused book record's. */
  bookTexts(): BookTexts {
    return { metadata: [...this.texts.metadata], theme: [...this.texts.theme] };
  }

  /** TL17–TL20: the template link the book piece carried; null for none, or before it is met. */
  bookTemplate(): ManifestTemplate | null {
    return this.link;
  }

  /** Table P: the records `pieces` (one batch, in table O′'s order) create. */
  async plan(pieces: readonly BundlePiece[]): Promise<PlannedBatch> {
    const ops: ImportOp[] = [];
    const found: Uint8Array[] = [];
    for (const piece of pieces) {
      switch (piece.type) {
        case 'book':
          // P1: kept for the commit.
          if (piece.metadata !== null) this.texts.metadata.push(piece.metadata);
          if (piece.theme !== null) this.texts.theme.push(piece.theme);
          this.link = piece.template;
          break;
        case 'chapter':
          ops.push(...(await this.chapter(piece)));
          break;
        case 'copy':
          ops.push(...[this.copy(piece)].filter((op) => op !== null));
          break;
        case 'snapshot':
          ops.push(...(await this.snapshot(piece)));
          break;
        case 'image':
          ops.push(...this.image(piece, found));
          break;
        case 'bookNote':
          ops.push(...[this.bookNote(piece)].filter((op) => op !== null));
          break;
        case 'comment':
          ops.push(...[await this.comment(piece)].filter((op) => op !== null));
          break;
        case 'note':
          ops.push(...[await this.note(piece)].filter((op) => op !== null));
          break;
        case 'shareLink':
          ops.push(...this.shareLink(piece));
          break;
        case 'share':
          ops.push(...this.share(piece));
          break;
        case 'refused':
          ops.push(...this.refused(piece, found));
          break;
      }
    }
    return { ops, found };
  }

  /**
   * P23: the recovered chapters' entries, live after `after` (the book's last chapter with a valid position, null for
   * none; by default the last the bundle's chapters gave), in the order found; and, when `pictures` holds any (each a
   * paragraph of picture markup), one chapter holding them.
   */
  finish(labels: RecoveredLabels, pictures: readonly string[], after: string | null = this.last): ImportOp[] {
    const { bookId } = this.options;
    const extra: ImportOp[] = [];
    const entries = [...this.recovered];
    if (pictures.length > 0) {
      const id = this.options.newId();
      entries.push({ id, bookId, title: labels.pictures, synopsis: '', subtitle: '', image: '', trashed: null });
      extra.push({ kind: 'add', store: 'chapters', value: { id, bookId, text: pictures.join('\n\n') } });
    }
    // Positions as short as bisection gives them, so any number fit; none past the length cap.
    const positions = spread(after, null, entries.length) ?? [];
    return [
      ...entries.map((entry, index): ImportOp => ({
        kind: 'add',
        store: 'entries',
        value: { ...entry, title: entry.title === '' ? labels.text : entry.title, position: positions[index] ?? '' },
      })),
      ...extra,
    ];
  }

  /**
   * C1, table CR (deep-fix r1): the book's ID for chapter `id` the bundle names: itself when restoring a chapter the bundle
   * brings (`brought`: an entry, CR1; 24b's O′ puts entries before anything naming them, CR2); otherwise one fresh ID per
   * chapter, whatever names it (CR3, CR4), so nothing of the bundle lands on a chapter of another book.
   */
  protected chapterId(id: string, brought = false): string {
    let mapped = this.chapters.get(id);
    if (mapped === undefined) {
      mapped = this.options.mode === 'restore' && brought ? id : this.options.newId();
      this.chapters.set(id, mapped);
    }
    return mapped;
  }

  /** C2: a record's ID: itself when restoring, fresh in a copy. */
  private ownId(id: string): string {
    return this.options.mode === 'restore' ? id : this.options.newId();
  }

  /** C2 (part 19e): note `id`'s ID in the book, the same wherever the bundle names it (a reply's parent, the note). */
  protected noteId(id: string): string {
    let mapped = this.notes.get(id);
    if (mapped === undefined) {
      mapped = this.ownId(id);
      this.notes.set(id, mapped);
    }
    return mapped;
  }

  /** MB1, MB2: the note a chapter's notes (a version 1 to 4 bundle's) become, under its ID in the book; none when empty. */
  protected chapterNotes(chapterId: string, notes: string): ImportOp[] {
    const note = chapterNotesNote(chapterId, { id: chapterId, bookId: this.options.bookId }, notes, this.options.now);
    if (note === null) return [];
    this.tally.notes += 1;
    return [{ kind: 'add', store: 'notes', value: note }];
  }

  /** P2, P3, MB2: a chapter's entry, its text when it has one, and its notes when a version 1 to 4 bundle gives some. */
  protected chapter(piece: ChapterPiece): ImportOp[] | Promise<ImportOp[]> {
    const { bookId } = this.options;
    const id = this.chapterId(piece.id, true);
    if (piece.trashed === null) {
      this.tally.chapters += 1;
      // Valid positions order by their code units (decision 62).
      if (isPosition(piece.position) && (this.last === null || piece.position > this.last)) this.last = piece.position;
    } else this.tally.trash += 1;
    const ops: ImportOp[] = [
      { kind: 'add', store: 'entries', value: { id, bookId, title: piece.title, position: piece.position, synopsis: piece.synopsis, subtitle: piece.subtitle, image: piece.image, trashed: piece.trashed } },
      ...this.chapterNotes(id, piece.notes),
    ];
    if (piece.text === null) this.textless.set(piece.id, id);
    else ops.push({ kind: 'add', store: 'chapters', value: { id, bookId, text: piece.text } }, ...this.handover(id, piece.text));
    return ops;
  }

  /**
   * Part 24d, H′2 (deep-fix r3): the text a bundle hands this store of chapter `chapterId` (its ID as this import makes it),
   * recorded in the import's own history: a `from-bundle` snapshot made now, the newest of the import's owner in the
   * chapter, pinned `bundle` for a year (`exportPinUntil`), so an update from a later bundle finds it as the version both
   * stores last shared.
   */
  protected handover(chapterId: string, text: string): ImportOp[] {
    const { bookId, owner, now, zone, newId } = this.options;
    const id = newId();
    const snapshot: Snapshot = { id, bookId, chapterId, owner, seq: HANDOVER_SEQ, createdAt: now, settled: null, zone, reason: 'from-bundle', pins: [{ kind: 'bundle', until: exportPinUntil(now) }] };
    return [
      { kind: 'add', store: 'snapshots', value: snapshot },
      { kind: 'add', store: 'snapshotTexts', value: { id, text } },
    ];
  }

  /** P4: a conflict copy (null: none, for a subclass that keeps one already held). */
  protected copy(piece: CopyPiece): ImportOp | null {
    this.tally.copies += 1;
    return {
      kind: 'add',
      store: 'conflictCopies',
      value: { id: this.ownId(piece.id), chapterId: this.chapterId(piece.chapterId), bookId: this.options.bookId, text: piece.text, createdAt: piece.createdAt, origin: piece.origin },
    };
  }

  /** P5, table H: a snapshot in the import's own history, and its text when it has one. */
  protected snapshot(piece: SnapshotPiece): ImportOp[] | Promise<ImportOp[]> {
    const { bookId, owner, now, mode } = this.options;
    const chapterId = this.chapterId(piece.chapterId);
    let owners = this.runs.get(chapterId);
    if (owners === undefined) {
      owners = new Map();
      this.runs.set(chapterId, owners);
    }
    let run = owners.get(piece.owner);
    if (run === undefined) {
      run = { ordinal: owners.size, count: 0, last: undefined };
      owners.set(piece.owner, run);
    }
    // Decision 29's clamp: never before the same owner's previous one in the chapter, never past the import time's slack.
    const createdAt = clampTime(piece.createdAt, run.last, now);
    const snapshot: Snapshot = {
      id: this.ownId(piece.id),
      bookId,
      chapterId,
      owner,
      seq: run.ordinal * OWNER_SPAN + run.count,
      createdAt,
      settled: null,
      zone: piece.zone,
      reason: piece.reason,
      pins: mode === 'copy' ? piece.pins.filter((pin) => pin.kind !== 'share-link') : piece.pins,
    };
    run.count += 1;
    run.last = createdAt;
    this.tally.snapshots += 1;
    const ops: ImportOp[] = [{ kind: 'add', store: 'snapshots', value: snapshot }];
    if (piece.text === null) this.textlessSnapshots.set(piece.id, snapshot.id);
    else ops.push({ kind: 'add', store: 'snapshotTexts', value: { id: snapshot.id, text: piece.text } });
    return ops;
  }

  /** P6–P8: a kept image under its ID within the cap; a found one's bytes for the pipeline; a missing one counted. */
  protected image(piece: ImagePiece, found: Uint8Array[]): ImportOp[] {
    const { bookId } = this.options;
    const { record, bytes } = piece;
    if (piece.state === 'found' && bytes !== null) {
      this.tally.found += 1;
      found.push(bytes);
      return [];
    }
    if (piece.state !== 'kept' || record === null || bytes === null || !this.reserve(record.size)) {
      this.tally.missing += 1;
      return [];
    }
    this.tally.pictures += 1;
    const image: ImageRecord = { bookId, id: piece.id, ...record };
    return [{ kind: 'add-image', image, bytes: { bookId, id: piece.id, bytes } }];
  }

  /** P9, MB3: a character or place note, as a Notebook note (null: none, as {@link copy}). */
  protected bookNote(piece: BookNotePiece): ImportOp | null {
    this.tally.notes += 1;
    const { kind, name, text, createdAt } = piece;
    return { kind: 'add', store: 'notes', value: bookNoteNote(this.noteId(piece.id), { id: piece.id, bookId: this.options.bookId, kind, name, text, createdAt }) };
  }

  /**
   * P10, CR5, MB4: a comment, as a Word note, its ID remade in a copy or on a chapter given a fresh ID (part 23c, DD);
   * null: none, as {@link copy}.
   */
  protected async comment(piece: CommentPiece): Promise<ImportOp | null> {
    const { bookId } = this.options;
    const chapterId = this.chapterId(piece.chapterId);
    this.tally.comments += 1;
    const id = await this.wordId(piece.id, chapterId, piece.chapterId, piece.sourceKey);
    const { sourceKey, origin, author, text, createdAt, storedAt, range, quote, before, after } = piece;
    return { kind: 'add', store: 'notes', value: commentNote(id, { id, bookId, chapterId, sourceKey, origin, author, text, createdAt, storedAt, range, quote, before, after }) };
  }

  /**
   * P10, CR5 (part 23c, DD): a Word note's ID: the bundle's while restoring onto the chapter it names, else made from the
   * book, the chapter (`chapterId`, the book's) and its key; one a reply named first keeps that (its thread, V5-6).
   */
  protected async wordId(id: string, chapterId: string, bundleChapter: string, sourceKey: string): Promise<string> {
    const { bookId, mode, digest } = this.options;
    const named = this.notes.get(id);
    if (named !== undefined) return named;
    const made = mode === 'restore' && chapterId === bundleChapter ? id : await commentId(digest, bookId, chapterId, sourceKey);
    this.notes.set(id, made);
    return made;
  }

  /**
   * V5-1 (part 19e): a note record of a version 5 bundle, on its chapter in the book (the Notebook's stays), its thread's
   * ID mapped as its own (C2), a Word note's ID as a comment's (P10); a tombstone counts as no note. Null: none, as
   * {@link copy}.
   */
  protected async note(piece: NotePiece): Promise<ImportOp | null> {
    if (piece.deletedAt === null) this.tally.notes += 1;
    return { kind: 'add', store: 'notes', value: await this.noteRecord(piece, piece.chapterId === null ? null : this.chapterId(piece.chapterId)) };
  }

  /** V5-1: `piece` as the record this book holds, on chapter `chapterId` (the book's ID for the bundle's). */
  protected async noteRecord(piece: NotePiece, chapterId: string | null): Promise<NoteRecord> {
    const word = piece.sourceKey !== null && chapterId !== null && piece.chapterId !== null;
    const id = word ? await this.wordId(piece.id, chapterId, piece.chapterId, piece.sourceKey) : this.noteId(piece.id);
    const { anchor, title, group, origin, author, sourceKey, visibility, resolved, text, writtenAt, storedAt, editedAt, deletedAt, link, shared } = piece;
    const parentId = piece.parentId === null ? null : this.noteId(piece.parentId);
    // V6-4 (part 38d, NR7): a note keeps the link's item it is, in a copy too (a copy holds no link record to reply with).
    // BV2, BV4 (part 38e): restored or staged for an update, it keeps its share part as the bundle has it, `sent` too.
    const note: NoteRecord = { id, bookId: this.options.bookId, chapterId, anchor, title, group, origin, author, sourceKey, visibility, parentId, resolved, text, writtenAt, storedAt, editedAt, deletedAt, ...(link === null ? {} : { link }), ...(shared === null ? {} : { shared }) };
    // BV3: a copy's notes are Only me.
    return this.options.mode === 'copy' ? copiedNote(note) : note;
  }

  /**
   * V6-4 (part 38d, L11): a reading link's record, restored under its ID with its chapters and snapshots as the bundle
   * names them (the book's own); a copy holds none (share-link records stay with the original book).
   */
  protected shareLink(piece: ShareLinkPiece): ImportOp[] {
    if (this.options.mode === 'copy') return [];
    return [{ kind: 'add', store: 'shareLinks', value: shareLinkRecordOf(piece, this.options.bookId) }];
  }

  /**
   * BU (part 38b, L10, IN13): a share's record, restored under its ID with its invites as the bundle holds them and nothing
   * pushed (this device pushed nothing, so every item goes again); a copy holds none (a copy isn't the shared book, L11).
   */
  protected share(piece: SharePiece): ImportOp[] {
    if (this.options.mode === 'copy') return [];
    const record = restoredShare(piece, this.options.bookId, this.options.now);
    return record === null ? [] : [{ kind: 'add', store: 'shares', value: record }];
  }

  /** P11–P22: what a refused record's words come back as. */
  protected refused(piece: RefusedPiece, found: Uint8Array[]): ImportOp[] {
    const { raw, key } = piece;
    switch (piece.store) {
      case 'books':
        // P11.
        for (const field of ['metadata', 'theme'] as const) {
          const text = rawField(raw, field);
          if (typeof text === 'string') this.texts[field].push(text);
        }
        return [];
      case 'entries':
        // P12, P13.
        return this.recover(isId(key) ? this.chapterId(key) : this.options.newId(), raw, piece.text);
      case 'chapters':
        // P14.
        return this.absorb(raw, takeOut(this.textless, key), 'chapters');
      case 'snapshotTexts':
        // P17.
        return this.absorb(raw, takeOut(this.textlessSnapshots, key), 'snapshotTexts');
      case 'conflictCopies': {
        // P15.
        const text = rawField(raw, 'text');
        return typeof text === 'string' ? this.recover(this.options.newId(), null, text) : [];
      }
      case 'snapshots':
        // P16.
        return piece.text === null ? [] : this.recover(this.options.newId(), null, piece.text);
      case 'comments': {
        // P19: a comment's text is plain, so it goes in as literal text.
        const text = rawField(raw, 'text');
        return typeof text === 'string' ? this.recover(this.options.newId(), null, escapeLiteral(text)) : [];
      }
      case 'bookNotes':
        return [this.recoverNote(raw)];
      case 'notes':
        // Part 19e: a refused note's words, as a Notebook note.
        return [this.recoverRecord(raw)];
      case 'imageBytes':
        // P21.
        if (piece.bytes !== null) {
          this.tally.found += 1;
          found.push(piece.bytes);
        }
        return [];
      case 'images':
      case 'device':
      case 'shareLinks':
      case 'shares':
        // P20, P22; V6-4, BU: a refused reading link or share holds no words, and its keys and secrets can't be trusted.
        return [];
    }
  }

  /**
   * P14, P17: a refused text record's raw `text`: the text of `into` (a chapter or snapshot of this import created with
   * none), else a recovered chapter holding it; nothing when it isn't a string.
   */
  private absorb(raw: JsonValue, into: string | null, store: 'chapters' | 'snapshotTexts'): ImportOp[] {
    const text = rawField(raw, 'text');
    if (typeof text !== 'string') return [];
    if (into === null) return this.recover(this.options.newId(), null, text);
    this.tally.recovered += 1;
    return store === 'chapters'
      ? [{ kind: 'add', store, value: { id: into, bookId: this.options.bookId, text } }]
      : [{ kind: 'add', store, value: { id: into, text } }];
  }

  /**
   * P12, P13, P15, P16, P19, P23: a recovered chapter `id`: its entry kept for {@link finish}, its fields from `raw` where
   * strings (null: none), its text created now when there is one.
   */
  private recover(id: string, raw: JsonValue | null, text: string | null): ImportOp[] {
    const { bookId } = this.options;
    const field = (key: string): string => (raw === null ? '' : rawString(raw, key, ''));
    this.recovered.push({ id, bookId, title: field('title'), synopsis: field('synopsis'), subtitle: field('subtitle'), image: field('image'), trashed: null });
    this.tally.recovered += 1;
    // MB5: a refused entry's notes, as the recovered chapter's note.
    return [...this.chapterNotes(id, field('notes')), ...(text === null ? [] : [{ kind: 'add', store: 'chapters', value: { id, bookId, text } } satisfies ImportOp])];
  }

  /** P18, MB6: a refused book note as a Notebook note of its own, with what its raw value holds where valid. */
  private recoverNote(raw: JsonValue): ImportOp {
    const kind = NOTE_KINDS.find((each) => each === rawField(raw, 'kind')) ?? 'character';
    this.tally.recovered += 1;
    const note = { id: this.options.newId(), bookId: this.options.bookId, kind, name: rawString(raw, 'name', ''), text: rawString(raw, 'text', ''), createdAt: rawCount(raw, 'createdAt', this.options.now) };
    return { kind: 'add', store: 'notes', value: bookNoteNote(note.id, note) };
  }

  /** Part 19e: a refused note record's words as a Notebook note of the writer's, with its raw title, group and text. */
  private recoverRecord(raw: JsonValue): ImportOp {
    this.tally.recovered += 1;
    const at = rawCount(raw, 'storedAt', this.options.now);
    const { now, newId, bookId } = this.options;
    const note: NoteRecord = {
      id: newId(),
      bookId,
      chapterId: null,
      anchor: null,
      title: rawString(raw, 'title', ''),
      group: rawString(raw, 'group', ''),
      origin: 'writer',
      author: '',
      sourceKey: null,
      visibility: 'only-me',
      parentId: null,
      resolved: false,
      text: rawString(raw, 'text', ''),
      writtenAt: null,
      storedAt: at,
      editedAt: now,
      deletedAt: null,
    };
    return { kind: 'add', store: 'notes', value: note };
  }
}
